import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Input — spec'd in handoff/COMPONENT_SPECS.md.
 *
 * 40px height, neutral-300 border, 4px radius. Optional `prefix` and
 * `suffix` slots render as bg-card-subtle segments separated by a border
 * (e.g. `https://` ... `.co.za`). `error` flips the border to danger.
 */

interface InputProps extends Omit<React.ComponentProps<'input'>, 'prefix' | 'suffix'> {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  error?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, prefix, suffix, error, disabled, ...props }, ref) => {
    if (!prefix && !suffix) {
      return (
        <input
          type={type}
          ref={ref}
          disabled={disabled}
          aria-invalid={error || undefined}
          className={cn(
            'flex h-10 w-full rounded border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
            'disabled:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground',
            error ? 'border-danger' : 'border-input',
            className
          )}
          {...props}
        />
      );
    }

    return (
      <div
        className={cn(
          'flex h-10 w-full items-stretch rounded border bg-card text-sm overflow-hidden',
          'focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2',
          error ? 'border-danger' : 'border-input',
          disabled && 'bg-muted cursor-not-allowed',
          className
        )}
      >
        {prefix && (
          <span
            className={cn(
              'flex items-center bg-muted px-3 text-muted-foreground border-r border-border',
              error && 'border-danger'
            )}
          >
            {prefix}
          </span>
        )}
        <input
          type={type}
          ref={ref}
          disabled={disabled}
          aria-invalid={error || undefined}
          className={cn(
            'flex-1 min-w-0 bg-transparent px-3 text-foreground placeholder:text-muted-foreground',
            'focus:outline-none disabled:cursor-not-allowed disabled:text-muted-foreground'
          )}
          {...props}
        />
        {suffix && (
          <span
            className={cn(
              'flex items-center bg-muted px-3 text-muted-foreground border-l border-border',
              error && 'border-danger'
            )}
          >
            {suffix}
          </span>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
export type { InputProps };
